import { useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { motion } from "framer-motion";
import { Gauge, Loader2, ShieldCheck, CheckCircle2, XCircle } from "lucide-react";

export default function ResetPassword() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState("");
  const [done, setDone] = useState(false);
  const [saving, setSaving] = useState(false);

  const { data, isLoading, isError } = useQuery({
    queryKey: ["reset-info", token],
    queryFn: async () => (await api.get(`/auth/reset-info?token=${encodeURIComponent(token)}`)).data,
    retry: false,
    enabled: Boolean(token),
  });

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (password.length < 6) return setError("Password must be at least 6 characters");
    if (password !== confirm) return setError("Passwords do not match");
    setSaving(true);
    try {
      await api.post("/auth/reset-password", { token, password });
      setDone(true);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || "Failed to reset password");
    } finally {
      setSaving(false);
    }
  };

  const invalid = !token || isError;

  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-6 py-12">
      <div className="tactical-grid pointer-events-none fixed inset-0 opacity-20" />
      <motion.div
        initial={{ opacity: 0, y: 16 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4 }}
        className="relative w-full max-w-sm rounded-2xl border border-border bg-card/90 p-8 shadow-2xl backdrop-blur"
      >
        <div className="mb-6 flex items-center gap-3">
          <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary shadow-lg shadow-primary/30">
            <Gauge className="h-5 w-5 text-white" />
          </div>
          <div>
            <p className="font-heading text-lg font-extrabold uppercase leading-none">Warranty Compliance Pro</p>
            <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground">Set your password</p>
          </div>
        </div>

        {isLoading && token ? (
          <div className="flex items-center gap-2 py-8 text-sm text-muted-foreground"><Loader2 className="h-4 w-4 animate-spin" /> Validating your link…</div>
        ) : invalid ? (
          <div data-testid="reset-invalid" className="space-y-4 py-4 text-center">
            <XCircle className="mx-auto h-10 w-10 text-red-400" />
            <p className="text-sm text-muted-foreground">This reset link is invalid or has expired. Ask your administrator to send a new invite.</p>
            <Button asChild variant="outline" className="w-full"><Link to="/login">Back to sign in</Link></Button>
          </div>
        ) : done ? (
          <div data-testid="reset-success" className="space-y-4 py-4 text-center">
            <CheckCircle2 className="mx-auto h-10 w-10 text-emerald-400" />
            <p className="text-sm text-muted-foreground">Your password has been set. You can now sign in.</p>
            <Button asChild className="w-full"><Link to="/login" data-testid="reset-goto-login">Go to sign in</Link></Button>
          </div>
        ) : (
          <form onSubmit={submit} className="space-y-4">
            <p className="flex items-center gap-2 font-mono text-[11px] uppercase tracking-widest text-primary">
              <ShieldCheck className="h-3.5 w-3.5" /> {data?.email}
            </p>
            <div>
              <Label className="mb-1.5 block font-mono text-[11px] uppercase tracking-wider text-muted-foreground">New Password</Label>
              <Input type="password" data-testid="reset-password-input" className="bg-secondary/40" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="min 6 characters" required />
            </div>
            <div>
              <Label className="mb-1.5 block font-mono text-[11px] uppercase tracking-wider text-muted-foreground">Confirm Password</Label>
              <Input type="password" data-testid="reset-confirm-input" className="bg-secondary/40" value={confirm} onChange={(e) => setConfirm(e.target.value)} required />
            </div>
            {error && <p data-testid="reset-error" className="rounded-md border border-red-500/30 bg-red-500/10 px-3 py-2 text-sm text-red-300">{error}</p>}
            <Button type="submit" disabled={saving} data-testid="reset-submit-button" className="w-full" size="lg">
              {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Set Password
            </Button>
          </form>
        )}
      </motion.div>
    </div>
  );
}
